/**
 * Ricoh support sessions — the record the console keeps across the reload.
 *
 * A switch through all-customers access opens a time-boxed support session
 * on the API (`switchCustomer(reason, ticketRef, hours)` →
 * `supportSessionExpiresAt`). The switch hard-reloads the app, so the
 * banner that says "Ricoh support session in {customer} · ends 17:40 · Back
 * to customers" needs the facts on the far side of the reload: which
 * customer, until when, and which customer is HOME to go back to.
 *
 * localStorage, under a key the switch's customer-scoped sweep never
 * clears, written AFTER that sweep. Every read re-validates against the
 * active `customer-id`: a record for another customer is stale (a second
 * switch, a sign-in as someone else) and is dropped.
 *
 * Pure — the service around it owns storage.
 */

export const SUPPORT_SESSION_KEY = 'ws-support-session';

export interface SupportSessionRecord {
  /** The customer the session is in. */
  customerId: string;
  customerName: string;
  /** ISO timestamp the API returned as `supportSessionExpiresAt`. */
  expiresAt: string;
  /** The operator's own organisation — where "Back to customers" goes. */
  homeCustomerId: string;
}

export function serialiseSupportSession(record: SupportSessionRecord): string {
  return JSON.stringify(record);
}

/** Parse a stored record; anything malformed is `null`. */
export function parseSupportSession(raw: string | null): SupportSessionRecord | null {
  if (!raw) return null;
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!value || typeof value !== 'object') return null;
  const r = value as Record<string, unknown>;
  const text = (key: string): string | null =>
    typeof r[key] === 'string' && (r[key] as string).length > 0 ? (r[key] as string) : null;
  const customerId = text('customerId');
  const expiresAt = text('expiresAt');
  const homeCustomerId = text('homeCustomerId');
  if (!customerId || !expiresAt || !homeCustomerId) return null;
  if (Number.isNaN(Date.parse(expiresAt))) return null;
  return {
    customerId,
    customerName: text('customerName') ?? '',
    expiresAt,
    homeCustomerId,
  };
}

/**
 * Whether a parked record still describes the active session: it must be
 * for the customer the app is signed into now. An expired record is KEPT —
 * the banner then says the session has ended and offers the way home — but
 * one for a different customer is not.
 */
export function recordMatchesActiveCustomer(
  record: SupportSessionRecord | null,
  activeCustomerId: string | null,
): boolean {
  return !!record && !!activeCustomerId && record.customerId === activeCustomerId;
}

/**
 * The HOME customer to remember when a new session starts: the one already
 * recorded if the operator hops from one session straight into another,
 * otherwise wherever they are now (which, outside a session, is home).
 */
export function homeForNewSession(
  existing: SupportSessionRecord | null,
  activeCustomerId: string | null,
): string | null {
  return existing?.homeCustomerId ?? activeCustomerId ?? null;
}

/** Whole minutes left, never negative; 0 once ended. */
export function minutesRemaining(expiresAt: string, now: number = Date.now()): number {
  const end = Date.parse(expiresAt);
  if (Number.isNaN(end)) return 0;
  return Math.max(0, Math.ceil((end - now) / 60_000));
}

export function hasEnded(expiresAt: string, now: number = Date.now()): boolean {
  return minutesRemaining(expiresAt, now) === 0;
}

/** The session lengths the sign-in dialog offers, in hours. */
export const SUPPORT_SESSION_HOURS: readonly number[] = [1, 2, 4, 8, 24];
export const SUPPORT_SESSION_DEFAULT_HOURS = 8;
/** Matches the API's bound (`SUPPORT_SESSION_REASON_MAX`). */
export const SUPPORT_SESSION_REASON_MAX = 500;
export const SUPPORT_SESSION_TICKET_MAX = 100;

/**
 * The sign-in note is optional — Ricoh support staff need not justify
 * signing in to a customer — but bounded like the API's reason; returns an
 * error key or null.
 */
export function reasonError(reason: string): 'too_long' | null {
  return reason.trim().length > SUPPORT_SESSION_REASON_MAX ? 'too_long' : null;
}
